import type { AuthAdapter, ExtraHeaders } from '../config';
import { AuthenticationRequiredError, EnterHttpError } from './errors';
import { normalizeHeaders } from './headers';
import type { EnterHttpClient, HttpRequestOptions } from './types';

function withDefaultBearer(
    headers: ExtraHeaders | undefined,
    token: string
): Record<string, string> {
    return {
        ...normalizeHeaders(headers),
        Authorization: `Bearer ${token}`,
    };
}

function createAbortError(): Error {
    const error = new Error('The request was aborted');
    error.name = 'AbortError';
    return error;
}

function throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) throw createAbortError();
}

/**
 * 在 baseHttp 之上增加默认用户 token。
 * token 获取和 401 回调都由宿主注入，避免公共包绑定具体登录 SDK。
 */
export function createAuthHttpClient(
    baseHttp: EnterHttpClient,
    authAdapter: AuthAdapter
): EnterHttpClient {
    let authRequiredPromise: Promise<void> | null = null;

    // 同一时间的多个鉴权失败只通知宿主一次，避免重复触发登录跳转。
    async function notifyAuthRequired(
        error: AuthenticationRequiredError | EnterHttpError,
        errorHandling: HttpRequestOptions['errorHandling']
    ): Promise<void> {
        if (errorHandling === 'caller' || !authAdapter.onAuthRequired) {
            return;
        }

        if (!authRequiredPromise) {
            authRequiredPromise = Promise.resolve(authAdapter.onAuthRequired(error)).finally(() => {
                authRequiredPromise = null;
            });
        }
        return authRequiredPromise;
    }

    async function request<T = unknown>(
        path: string,
        options: HttpRequestOptions = {}
    ): Promise<T> {
        try {
            throwIfAborted(options.signal);
            let token: string | null | undefined;
            try {
                token = await authAdapter.getAccessToken();
            } catch (error) {
                throwIfAborted(options.signal);
                const authError = new AuthenticationRequiredError(
                    'Unable to acquire an access token',
                    error
                );
                await notifyAuthRequired(authError, options.errorHandling);
                throw authError;
            }

            throwIfAborted(options.signal);
            if (!token) {
                const authError = new AuthenticationRequiredError();
                await notifyAuthRequired(authError, options.errorHandling);
                throw authError;
            }

            return await baseHttp.request<T>(path, {
                ...options,
                headers: withDefaultBearer(options.headers, token),
            });
        } catch (error) {
            throwIfAborted(options.signal);
            if (error instanceof EnterHttpError && error.status === 401) {
                await notifyAuthRequired(error, options.errorHandling);
            }
            throw error;
        }
    }

    return {
        request,
        get: (path, options) => request(path, { ...options, method: 'GET' }),
        post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
        put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
        patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
        delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
    };
}
